import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";

import { styles } from "./donation-progress-bar.css";

/**
 * Donation Progress Bar
 *
 * Pure-presentation component for live-event donation progress. Two variants:
 * - `thin`: bar only (used in modal headers / cards alongside other content).
 * - `banner`: full presentation with "Totaal opgehaald" / "Streefbedrag" labels
 *   and formatted amounts, matching Figma node 1170-698.
 *
 * Localized labels are inlined as a fallback. Task 22 of the live-event-donations
 * plan replaces the strings with t() calls.
 */
@customElement("donation-progress-bar")
export class DonationProgressBar extends LitElement {
  static override styles = [styles];

  @property({ type: Number, attribute: "target-cents" }) targetCents = 0;
  @property({ type: Number, attribute: "raised-cents" }) raisedCents = 0;
  @property({ type: Number, attribute: "donor-count" }) donorCount = 0;
  @property({ type: String }) variant: "thin" | "banner" = "banner";
  @property({ type: Boolean, attribute: "show-label" }) showLabel = true;
  @property({ type: String }) locale = "nl-NL";
  @property({ type: String }) primaryColor: string | undefined;
  @property({ type: String }) secondaryColor: string | undefined;

  private formatEur(cents: number): string {
    const value = cents / 100;
    const formatter = new Intl.NumberFormat(this.locale, {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    const formatted = formatter.format(value);
    // Whole-euro amounts in NL get the "comma-dash" suffix to match Figma "€10.685,-"
    if (this.locale.startsWith("nl") && cents % 100 === 0) {
      return formatted.replace(/(\d)$/, "$1,-");
    }
    return formatted;
  }

  private get fillPercent(): number {
    if (this.targetCents <= 0) return 0;
    return Math.min(100, Math.round((this.raisedCents / this.targetCents) * 100));
  }

  override render() {
    if (this.variant === "thin") {
      return html`
        <div
          class="bar thin"
          role="progressbar"
          aria-valuenow=${this.fillPercent}
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div class="bar-fill" style="width: ${this.fillPercent}%"></div>
        </div>
      `;
    }

    const fill = this.fillPercent;
    const fillClass = fill === 100 ? "bar-fill solo" : "bar-fill";
    const emptyClass = fill === 0 ? "bar-empty solo" : "bar-empty";
    const themeStyle = [
      this.primaryColor ? `--donation-banner-bg: ${this.primaryColor};` : "",
      this.secondaryColor ? `--donation-banner-fg: ${this.secondaryColor};` : "",
    ]
      .filter(Boolean)
      .join(" ");

    return html`
      <div
        class="banner"
        role="progressbar"
        aria-valuenow=${this.fillPercent}
        aria-valuemin="0"
        aria-valuemax="100"
        style=${themeStyle}
      >
        <div class="row labels">
          <span class="raised-label">${this.tRaisedLabel()}</span>
          <span class="target-label">${this.tTargetLabel()}</span>
        </div>
        <div class="row amounts">
          <span class="raised-amount">${this.formatEur(this.raisedCents)}</span>
          <span class="target-amount">${this.formatEur(this.targetCents)}</span>
        </div>
        <div class="bar full">
          ${fill > 0 ? html`<div class=${fillClass} style="width: ${fill}%"></div>` : ""}
          ${fill < 100 ? html`<div class=${emptyClass}></div>` : ""}
        </div>
      </div>
    `;
  }

  // Inline language strings as a fallback — Task 22 replaces these with t() calls.
  private tRaisedLabel(): string {
    return this.locale.startsWith("nl") ? "Totaal opgehaald:" : "Total raised:";
  }
  private tTargetLabel(): string {
    return this.locale.startsWith("nl") ? "Streefbedrag" : "Target";
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "donation-progress-bar": DonationProgressBar;
  }
}
